import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    crawlId: Number,
    currentUserId: Number,
    autoLoad: { type: Boolean, default: false },
  }
  static targets = ["messages", "input", "toggle", "panel", "charCount"]

  connect() {
    this.messages = []
    this.isOpen = false
    this.pollInterval = null
    this.lastMessageId = null

    // Auto-load on dedicated chat page
    if (this.autoLoadValue) {
      this.loadMessages()
      this.startPolling()
    }
  }

  disconnect() {
    this.stopPolling()
  }

  toggle() {
    this.isOpen = !this.isOpen
    if (this.isOpen) {
      this.panelTarget.classList.remove("hidden")
      this.toggleTarget.textContent = "Hide Chat"
      this.loadMessages()
      this.startPolling()
    } else {
      this.panelTarget.classList.add("hidden")
      this.toggleTarget.textContent = "Show Chat"
      this.stopPolling()
    }
  }

  async loadMessages() {
    try {
      const response = await fetch(`/api/v1/crawls/${this.crawlIdValue}/messages`, {
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
      })

      if (!response.ok) {
        if (response.status === 403) {
          this.messagesTarget.replaceChildren()
          const p = document.createElement("p")
          p.className = "text-gray-500 text-sm italic p-4"
          p.textContent = "Join the crawl to access chat."
          this.messagesTarget.appendChild(p)
          return
        }
        throw new Error("Failed to load messages")
      }

      const data = await response.json()
      this.messages = data.messages
      this.renderMessages()
      this.scrollToBottom()

      if (this.messages.length > 0) {
        this.lastMessageId = this.messages[this.messages.length - 1].id
      }
    } catch (error) {
      console.error("Chat load error:", error)
    }
  }

  async send(event) {
    event.preventDefault()
    const body = this.inputTarget.value.trim()
    if (!body) return

    try {
      const response = await fetch(`/api/v1/crawls/${this.crawlIdValue}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({ body }),
      })

      if (!response.ok) throw new Error("Failed to send message")

      this.inputTarget.value = ""
      this.updateCharCount()
      await this.loadMessages()
    } catch (error) {
      console.error("Chat send error:", error)
    }
  }

  updateCharCount() {
    if (!this.hasCharCountTarget) return
    const count = this.inputTarget.value.length
    this.charCountTarget.textContent = count > 450 ? `${count}/500` : ""
    this.charCountTarget.classList.toggle("text-red-400", count >= 500)

    if (count > 500) {
      this.inputTarget.value = this.inputTarget.value.substring(0, 500)
    }
  }

  renderMessages() {
    this.messagesTarget.replaceChildren()

    if (this.messages.length === 0) {
      const p = document.createElement("p")
      p.className = "text-gray-500 text-sm italic p-4 text-center"
      p.textContent = "No messages yet. Start the conversation!"
      this.messagesTarget.appendChild(p)
      return
    }

    this.messages.forEach((msg) => {
      const isOwn = msg.user_id === this.currentUserIdValue

      const wrapper = document.createElement("div")
      wrapper.className = `flex ${isOwn ? "justify-end" : "justify-start"} px-3 py-1`

      const bubble = document.createElement("div")
      bubble.className = `max-w-[80%] rounded-2xl px-4 py-2 ${isOwn ? "bg-[#f3c023] text-black rounded-br-md" : "bg-[#2C2C2E] text-white rounded-bl-md"}`

      if (!isOwn) {
        const nameSpan = document.createElement("span")
        nameSpan.className = "block text-xs font-semibold text-[#f3c023] mb-0.5"
        nameSpan.textContent = msg.user_name
        bubble.appendChild(nameSpan)
      }

      const bodyP = document.createElement("p")
      bodyP.className = "text-sm"
      bodyP.textContent = msg.body
      bubble.appendChild(bodyP)

      const timeSpan = document.createElement("span")
      timeSpan.className = `block text-[10px] mt-1 ${isOwn ? "text-black/60" : "text-gray-500"}`
      timeSpan.textContent = this.formatTime(msg.created_at)
      bubble.appendChild(timeSpan)

      wrapper.appendChild(bubble)
      this.messagesTarget.appendChild(wrapper)
    })
  }

  startPolling() {
    this.pollInterval = setInterval(() => this.loadMessages(), 5000)
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  formatTime(isoString) {
    try {
      const date = new Date(isoString)
      return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    } catch {
      return ""
    }
  }

  get csrfToken() {
    return document.querySelector('[name="csrf-token"]')?.getAttribute("content")
  }
}
